import React from 'react';
import { Typography } from '@vibTheme/components/_base/Typography';

export const HeaderCell = ({ children }: { children: React.ReactNode }) => (
  <th className="text-left px-md py-sm text-xs font-semibold text-lightBlack whitespace-nowrap">
    {children}
  </th>
);

export const Cell = ({
  children,
  muted,
}: {
  children: React.ReactNode;
  muted?: boolean;
}) => (
  <td
    className={`px-md py-sm text-sm whitespace-nowrap ${
      muted ? 'text-lightBlack' : 'text-[#374151]'
    }`}
  >
    {children}
  </td>
);

/**
 * A table that scrolls inside itself.
 *
 * The horizontal scroll container is deliberate: these are eight to eleven columns, and letting the page
 * scroll sideways instead would move the whole layout on a phone.
 */
export const ReportTable = ({
  headers,
  children,
  emptyMessage = 'Nenhum período para exibir.',
  isEmpty,
}: {
  headers: string[];
  children: React.ReactNode;
  emptyMessage?: string;
  isEmpty: boolean;
}) => {
  if (isEmpty)
    return (
      <Typography className="text-sm text-lightBlack">
        {emptyMessage}
      </Typography>
    );

  return (
    <div className="overflow-x-auto shadow-default rounded-default">
      <table className="w-full border-collapse">
        <thead className="bg-[#F9FAFB]">
          <tr>
            {headers.map((header) => (
              <HeaderCell key={header}>{header}</HeaderCell>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
};

export const Row = ({ children }: { children: React.ReactNode }) => (
  <tr className="border-t border-[#E5E7EB]">{children}</tr>
);

/**
 * A card with one figure and a line saying what it is over.
 *
 * The subtitle is not decoration. Half the numbers in these reports are over a denominator the reader
 * would otherwise guess wrong - a rate over arrivals sitting beside one over conversions, an ltv that
 * keeps growing after its month closed - and the card is where that gets said.
 */
export const StatCard = ({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) => (
  <div className="shadow-default rounded-default p-main">
    <Typography className="text-xs text-lightBlack">{label}</Typography>
    <Typography className="text-xl font-semibold text-[#374151]">
      {value}
    </Typography>
    {hint && (
      <Typography className="text-xs text-lightBlack mt-1">{hint}</Typography>
    )}
  </div>
);

export const StatGrid = ({ children }: { children: React.ReactNode }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-main mb-main">
    {children}
  </div>
);
